import React from 'react';
import { formatMoney, theme } from '../constants';
import { text } from '../text';
import { hooks } from '../hooks';
import { custom } from '../custom';
import { product } from '../product';
import { Box } from '@mui/material';

type Props = {
  item: any;
  isLast?: boolean;
  version?: number;
  isDiscount?: boolean;
};

export const CaregoryProductCard: React.FC<Props> = ({
  isLast,
  item,
  version = 1,
  isDiscount = false,
}) => {
  const navigate = hooks.useAppNavigate();

  // Home > BestSeller item //
  if (version === 1) {
    return (
      <Box
        sx={{
          width: '280px',
          height: '300px',
          borderRadius: 3,
          marginBottom: '20px',
          cursor: 'pointer',
          marginRight: isLast ? '20px' : '14px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
          padding: '8px',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.12)',
          },
        }}
        onClick={() => navigate('/product', { state: { item } })}
      >
        <custom.ImageBackground
          imageUrl={
            isDiscount
              ? item.attachments.product_discount
                ? item.attachments.product_discount
                : item.attachments.product_main
              : item.attachments.product_main
          }
          style={{
            width: 250,
            height: 250,
            borderRadius: 10,
            marginBottom: 14,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <product.ProductSales
            item={item}
            style={{ position: 'absolute', padding: 14, left: 0, top: 0 }}
          />
          <product.ProductInWishlist
            item={item}
            style={{ position: 'absolute', padding: 14, right: 0, top: 0 }}
          />
          <product.ProductInCart
            item={item}
            style={{ position: 'absolute', right: 0, bottom: 0, padding: 14 }}
          />
        </custom.ImageBackground>

        {item.default_sales_type === 'PACK' && (
          <Box
            sx={{
              fontSize: 14,
              fontFamily: theme.fonts.DM_Sans_400Regular,
              color: theme.colors.steelTeal,
              padding: '6px 8px',
              backgroundColor: theme.colors.ghostWhite,

              borderRadius: '6px',
              marginBottom: '8px',
            }}
          >
            <Box>{`Minimum Order: ${item.unit_per_pack} Items`}</Box>
            <Box>{` ETB ${formatMoney(item.price)} per piece * ${item.unit_per_pack} Items`}</Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 4px',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0, // Important for text truncation
            }}
          >
            <product.ProductName
              item={item.length > 20 ? item.substring(0, 15) + '...' : item}
              style={{
                marginBottom: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px',
                fontWeight: 500,
                fontSize: '15px',
              }}
            />
          </Box>
          <Box>
            <product.ProductPrice item={item} />
          </Box>
        </Box>
      </Box>
    );
  }

  // Shop Item
  if (version === 2) {
    return (
      <Box
        sx={{
          width: `calc(50% - 7.5px)`,
          cursor: 'pointer',
          borderRadius: 2,
          marginBottom: '20px',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
          transition: 'transform 0.2s',
          backgroundColor: 'white',
          overflow: 'hidden',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        }}
        onClick={() => {
          navigate('/product', { state: { item } });
        }}
      >
        <custom.ImageBackground
          imageUrl={item.image}
          style={{
            width: '100%',
            height: 170,
            borderRadius: '10px 10px 0 0',
            marginBottom: 6,
            position: 'relative',
            backgroundSize: 'contain',
            backgroundColor: theme.colors.imageBackground,
          }}
        >
          <product.ProductInWishlist
            item={item}
            style={{ position: 'absolute', padding: 10, right: 0, top: 0 }}
          />
          <product.ProductInCart
            item={item}
            style={{ position: 'absolute', right: 0, bottom: 0, padding: 10 }}
          />
        </custom.ImageBackground>
        <Box sx={{ padding: '8px 12px 12px' }}>
          <product.ProductName
            item={item}
            style={{
              marginBottom: 3,
              fontWeight: 500,
              fontSize: '14px',
            }}
          />
          {/* <product.ProductPrice item={item} /> */}
        </Box>
      </Box>
    );
  }

  // Featured Item
  if (version === 3) {
    const blockWidth = 138;

    return (
      <Box
        sx={{
          width: blockWidth,
          cursor: 'pointer',
          marginRight: isLast ? '20px' : '14px',
          borderRadius: '12px',
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
          transition: 'transform 0.2s',
          backgroundColor: 'white',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        }}
        onClick={() => {
          navigate('/product', { state: { item } });
        }}
      >
        <custom.ImageBackground
          imageUrl={item.image}
          style={{
            width: blockWidth,
            height: 170,
            borderRadius: '10px 10px 0 0',
            marginBottom: 6,
            position: 'relative',
            backgroundSize: 'contain',
            backgroundColor: theme.colors.imageBackground,
          }}
        >
          <product.ProductInWishlist
            item={item}
            style={{ position: 'absolute', padding: 10, right: 0, top: 0 }}
          />
          <product.ProductInCart
            item={item}
            style={{ position: 'absolute', padding: 10, right: 0, bottom: 0 }}
          />
        </custom.ImageBackground>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: '8px',
          }}
        >
          <product.ProductName
            item={item}
            style={{
              marginBottom: 2,
              marginRight: 2,
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '13px',
            }}
          />
          <Box sx={{ marginLeft: '4px', flexShrink: 0 }}>
            <product.ProductPrice item={item} />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};
